import {
  DeploymentChannel,
  DeploymentStatus,
  FieldEditability,
  FieldSource,
  FieldVisibility,
  SignatureProfileStatus,
  TemplateKind,
  TemplateVariant,
} from '../enums/signature.enum';
import { ISignatureField, ISignatureProfile } from '../models/ISignatureProfile.interface';
import { ISignatureFieldPolicy, ISignatureTemplate } from '../models/ISignatureTemplate.interface';

/**
 * In-memory stand-in for bc-signatures-api, used only when `environment.useSignatureFixtures`
 * is on. See `signature-fixtures.interceptor.ts` for how it is wired and why.
 *
 * The shapes here are the ones in `backend-todo-signatures.md` §2 and §6, not invented ones. That
 * is deliberate: if these fixtures satisfy every screen, the specification the API will be built
 * against is complete, and if a screen needs something the fixtures cannot express, the spec has a
 * gap worth finding now rather than after the service is written.
 */

/** The three group legal entities a person signs for. */
export const FIXTURE_ENTITIES: { id: number; name: string }[] = [
  { id: 1, name: 'BCT' },
  { id: 2, name: 'Adiuvo Trustees' },
  { id: 3, name: 'ATCA' },
];

/**
 * The field catalogue from §6.1, with the default policy each seeded template carries.
 *
 * `SignatureFieldDefinition` is the one entity with no tenant column — a global catalogue — so a
 * single list backing every entity's templates is faithful rather than a shortcut.
 *
 * **Every field is `UserEditable`, which is a deliberate departure from §6.1.** The specification
 * locks the firm's wording — the regulatory line, the disclaimer, the website — and routes name,
 * job title and department through the HR change-request flow. That is the right end state and it
 * assumes two things that do not exist yet: the §5 directory sync to own the HR values, and a
 * screen for an administrator to set the entity defaults. Without them, a locked field is simply a
 * field nobody can fill, and `jobTitle` being Required made every profile unsaveable.
 *
 * The consequence worth knowing: compliance wording is now per-person, so it can drift, and a
 * change to an entity default no longer reaches anyone who has edited their own copy. Narrowing a
 * field back is a policy row per template, not a code change — which is the whole reason policy is
 * data.
 */
export const FIXTURE_CATALOGUE: ISignatureFieldPolicy[] = [
  { fieldKey: 'fullName',       label: 'Full name',       visibility: FieldVisibility.Required, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: null, maxLength: 120 },
  { fieldKey: 'preferredName',  label: 'Preferred name',  visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: null, maxLength: 60 },
  { fieldKey: 'designations',   label: 'Designations',    visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable , lockedReason: null, validationRegex: null, maxLength: 80 },
  { fieldKey: 'jobTitle',       label: 'Job title',       visibility: FieldVisibility.Required, editability: FieldEditability.UserEditable , lockedReason: null, validationRegex: null, maxLength: 120 },
  { fieldKey: 'department',     label: 'Department',      visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable , lockedReason: null, validationRegex: null, maxLength: 80 },
  { fieldKey: 'pronouns',       label: 'Pronouns',        visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: null, maxLength: 30 },
  { fieldKey: 'email',          label: 'Email',           visibility: FieldVisibility.Required, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$', maxLength: 160 },
  { fieldKey: 'directPhone',    label: 'Direct line',     visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable , lockedReason: null, validationRegex: '^\\+[1-9]\\d{7,14}$', maxLength: 20 },
  { fieldKey: 'mobile',         label: 'Mobile',          visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^\\+[1-9]\\d{7,14}$', maxLength: 20 },
  { fieldKey: 'officeId',       label: 'Office',          visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable , lockedReason: null, validationRegex: null, maxLength: 60 },
  // The user's, not the firm's: people sit in different offices. The entity default still fills
  // the box, so it only differs for someone who changes it.
  { fieldKey: 'addressBlock',   label: 'Address',         visibility: FieldVisibility.Required, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: null, maxLength: 300 },
  { fieldKey: 'website',        label: 'Website',         visibility: FieldVisibility.Required, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^https://', maxLength: 120 },
  { fieldKey: 'bookingLink',    label: 'Meeting link',    visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^https://', maxLength: 200 },
  { fieldKey: 'linkedIn',       label: 'LinkedIn',        visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^https://([a-z]{2,3}\\.)?linkedin\\.com/', maxLength: 200 },
  // §6.1 excluded these deliberately, and said that if they were asked for they go in through
  // the field catalogue rather than a code change. This is that route: rows here, a policy per
  // template, and a Social block in the designer — no new column, no migration.
  { fieldKey: 'facebook',       label: 'Facebook',        visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^https://(www\\.)?facebook\\.com/', maxLength: 200 },
  { fieldKey: 'instagram',      label: 'Instagram',       visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^https://(www\\.)?instagram\\.com/', maxLength: 200 },
  { fieldKey: 'x',              label: 'X',               visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^https://(www\\.)?(x|twitter)\\.com/', maxLength: 200 },
  { fieldKey: 'youtube',        label: 'YouTube',         visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^https://(www\\.)?youtube\\.com/', maxLength: 200 },
  // wa.me is the link format WhatsApp itself publishes; a bare number would not be clickable
  // from a mail client, which is the only place these are ever pressed.
  { fieldKey: 'whatsapp',       label: 'WhatsApp',        visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^https://(wa\\.me|api\\.whatsapp\\.com)/', maxLength: 200 },
  { fieldKey: 'telegram',       label: 'Telegram',        visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^https://(t\\.me|telegram\\.me)/', maxLength: 200 },
  { fieldKey: 'mapLink',        label: 'Map link',        visibility: FieldVisibility.Optional, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^https://', maxLength: 300 },
  { fieldKey: 'regulatoryLine', label: 'Regulatory line', visibility: FieldVisibility.Required, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: null, maxLength: 600 },
  { fieldKey: 'disclaimer',     label: 'Disclaimer',      visibility: FieldVisibility.Required, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: null, maxLength: 2000 },
  { fieldKey: 'language',       label: 'Language',        visibility: FieldVisibility.Required, editability: FieldEditability.UserEditable,  lockedReason: null, validationRegex: '^(en|el)$', maxLength: 2 },
];

/** Which catalogue entries come from where, per §6.1's "Default source" column. */
const SOURCES: Record<string, FieldSource> = {
  fullName: FieldSource.Directory,
  preferredName: FieldSource.User,
  designations: FieldSource.Directory,
  jobTitle: FieldSource.Directory,
  department: FieldSource.Directory,
  pronouns: FieldSource.User,
  email: FieldSource.Directory,
  directPhone: FieldSource.Directory,
  mobile: FieldSource.User,
  officeId: FieldSource.Directory,
  addressBlock: FieldSource.EntityDefault,
  website: FieldSource.EntityDefault,
  bookingLink: FieldSource.User,
  linkedIn: FieldSource.User,
  facebook: FieldSource.User,
  instagram: FieldSource.User,
  x: FieldSource.User,
  youtube: FieldSource.User,
  whatsapp: FieldSource.User,
  telegram: FieldSource.User,
  mapLink: FieldSource.EntityDefault,
  regulatoryLine: FieldSource.EntityDefault,
  disclaimer: FieldSource.EntityDefault,
  language: FieldSource.User,
};

/** Last directory sync, as an already-past timestamp so the "synced from HR" hint has something. */
const SYNCED_AT = '2026-08-29T02:14:00Z';

/** Per-entity defaults — the `EntityDefault` fields plus the website each entity signs with. */
/**
 * Per-entity branding, including the image URLs a template reads.
 *
 * The paths point at BC_UI's own bundled assets, which is right for the preview and wrong in a
 * real mailbox — a recipient's mail client resolves them against its own host. §2.8 is explicit
 * that email-referenced images need a public CDN with stable URLs, and the Install screen warns
 * about exactly this. `signOffImageUrl` is left unset: no such asset is bundled, and the block
 * simply does not render, which is the guard doing its job.
 */
export const FIXTURE_ENTITY_BRANDING: Record<number, Record<string, string>> = {
  1: {
    addressBlock: '12 Themistokli Dervi, Office 401, 1066 Nicosia, Cyprus',
    website: 'https://bizcombo.tech',
    mapLink: 'https://maps.google.com/?q=12+Themistokli+Dervi,+1066+Nicosia,+Cyprus',
    regulatoryLine: 'BCT is regulated by the Institute of Certified Public Accountants of Cyprus (ICPAC), licence AF/2019/118.',
    disclaimer: 'This email and any attachments are confidential and intended solely for the addressee. If you have received it in error, please notify the sender and delete it. BCT accepts no liability for any loss arising from the use of this email or its attachments.',
    logoUrl: '/assets/logo-letter.png',
    socialIconBaseUrl: '/assets/signature-icons',
  },
  2: {
    addressBlock: '3 Agiou Pavlou, Ledra House, 1105 Nicosia, Cyprus',
    website: 'https://adiuvotrustees.com',
    mapLink: 'https://maps.google.com/?q=3+Agiou+Pavlou,+1105+Nicosia,+Cyprus',
    regulatoryLine: 'Adiuvo Trustees Ltd is licensed by the Cyprus Securities and Exchange Commission as an Administrative Services Provider, licence ASP 145/196.',
    disclaimer: 'This email and any attachments are confidential and intended solely for the addressee. Fiduciary services are provided under the terms of our engagement letter. If you have received this message in error, please notify the sender and delete it.',
    logoUrl: '/assets/logo-letter.png',
    socialIconBaseUrl: '/assets/signature-icons',
  },
  3: {
    addressBlock: '28 Spyrou Kyprianou, 4th floor, 3070 Limassol, Cyprus',
    website: 'https://atca.com.cy',
    mapLink: 'https://maps.google.com/?q=28+Spyrou+Kyprianou,+3070+Limassol,+Cyprus',
    regulatoryLine: 'ATCA Ltd is registered with the Cyprus Bar Association as a corporate services provider, registration CBA/CSP/0442.',
    disclaimer: 'This email and any attachments are confidential and may be legally privileged. Unauthorised use or disclosure is prohibited. If you have received this message in error, please notify the sender immediately and delete it.',
    logoUrl: '/assets/logo-letter.png',
    socialIconBaseUrl: '/assets/signature-icons',
  },
};

/**
 * Who the fixtures are about.
 *
 * The name and mailbox come from the signed-in MSAL account, not from a hardcoded person: a
 * signature demo showing somebody else's name is the one thing guaranteed to read as broken, and
 * it hides the bug where a real value fails to reach the template. Everything the token cannot
 * prove — job title, department, desk line — stays invented, because in production those come from
 * the HR record and the fixtures have no HR record to read.
 *
 * Mutated once by `applyFixtureIdentity` before the first seed. Defaults stand in for a signed-out
 * dev session, where there is no account to read.
 */
export interface FixtureIdentity {
  fullName?: string;
  preferredName?: string;
  email?: string;
  jobTitle?: string;
}

/** Returns true once a real name has landed, which is what the caller latches on. */
export function applyFixtureIdentity(identity: FixtureIdentity): boolean {
  if (identity.fullName) DIRECTORY_VALUES['fullName'] = identity.fullName;
  if (identity.preferredName) DIRECTORY_VALUES['preferredName'] = identity.preferredName;
  if (identity.email) DIRECTORY_VALUES['email'] = identity.email;
  if (identity.jobTitle) DIRECTORY_VALUES['jobTitle'] = identity.jobTitle;
  return !!identity.fullName;
}

/** The signed-in person, as the directory would return them. Same human across all three entities. */
const DIRECTORY_VALUES: Record<string, string> = {
  // Replaced by applyFixtureIdentity with the signed-in person; the placeholder only stands in for
  // a signed-out dev session.
  fullName: 'Not signed in',
  preferredName: '',
  email: '',
  // Everything below starts empty on purpose. That is what a new profile genuinely looks like —
  // the directory sync that would fill these does not exist yet — and inventing a job title here
  // would hide the only thing worth seeing: the field is the user's to complete.
  designations: '',
  jobTitle: '',
  department: '',
  pronouns: '',
  directPhone: '',
  mobile: '',
  officeId: '',
  bookingLink: '',
  linkedIn: '',
  facebook: '',
  instagram: '',
  x: '',
  youtube: '',
  whatsapp: '',
  telegram: '',
  language: 'en',
};

function buildFields(entityId: number, overrides: Record<string, string> = {}): ISignatureField[] {
  const entity = FIXTURE_ENTITY_BRANDING[entityId];
  return FIXTURE_CATALOGUE.map(policy => {
    const key = policy.fieldKey;
    const source = SOURCES[key] ?? FieldSource.User;
    const value = overrides[key] ?? entity[key] ?? DIRECTORY_VALUES[key] ?? '';
    return {
      key,
      label: policy.label,
      value: value === '' ? null : value,
      source,
      // Only Directory-sourced values carry a sync stamp; a user's own mobile was never synced.
      lastSyncedAtUtc: source === FieldSource.Directory ? SYNCED_AT : null,
      visibility: policy.visibility,
      editability: policy.editability,
      lockedReason: policy.lockedReason,
      validationRegex: policy.validationRegex,
      maxLength: policy.maxLength,
    };
  });
}

/**
 * Three profiles, one per entity, each parked in a different status.
 *
 * That spread is the point: BCT exercises the live-in-mailbox branch (which hides the copy button),
 * Adiuvo the editable draft, and ATCA the read-only pending-approval state. One profile would leave
 * two thirds of the screen's behaviour untested.
 */
export function seedProfiles(): ISignatureProfile[] {
  return [
    {
      id: 'p-bct-0001',
      name: null,
      legalEntityId: 1,
      legalEntityName: 'BCT',
      status: SignatureProfileStatus.Approved,
      isDefault: true,
      templateId: 't-bct-primary',
      templateName: 'BCT — Primary (EN)',
      templateVersion: 3,
      requiresApproval: true,
      rowVersion: 'AAAAAAAAB9E=',
      fields: buildFields(1),
      deployment: {
        channel: DeploymentChannel.GraphMailboxSettings,
        status: DeploymentStatus.Succeeded,
        completedAtUtc: '2026-08-21T09:14:00Z',
      },
    },
    {
      id: 'p-adv-0002',
      name: null,
      legalEntityId: 2,
      legalEntityName: 'Adiuvo Trustees',
      status: SignatureProfileStatus.Draft,
      isDefault: false,
      templateId: 't-adv-primary',
      templateName: 'Adiuvo — Primary (EN)',
      templateVersion: 1,
      requiresApproval: true,
      rowVersion: 'AAAAAAAAB40=',
      fields: buildFields(2),
      deployment: null,
    },
    {
      id: 'p-atc-0003',
      name: null,
      legalEntityId: 3,
      legalEntityName: 'ATCA',
      status: SignatureProfileStatus.PendingApproval,
      isDefault: false,
      templateId: 't-atc-primary',
      templateName: 'ATCA — Primary (EL)',
      templateVersion: 2,
      requiresApproval: true,
      rowVersion: 'AAAAAAAAB5g=',
      fields: buildFields(3, { language: 'el' }),
      deployment: {
        channel: DeploymentChannel.ExchangeTransport,
        status: DeploymentStatus.Pending,
        completedAtUtc: null,
      },
    },
  ];
}

/**
 * Template source. Table-based, every style inline, no `<style>` block and no `class=` attribute —
 * the constraints in §7.1 exist because Outlook silently drops all three, so the fixture obeys
 * them rather than producing markup the real renderer could never emit.
 *
 * **Every optional field is wrapped in `{{#if}}`, and that is what keeps a blank field out of the
 * signature.** Each guard takes the field's label and its separator with it, so someone with no
 * direct line gets no "D", and someone with no department gets no trailing "·" — rather than the
 * punctuation of a value that is not there. A row whose cells all end up empty is dropped by the
 * renderer. These guards mirror the starter templates the server seeds.
 */
const PRIMARY_BODY = `<table cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;font-family:Segoe UI,Arial,sans-serif;font-size:12px;color:#333333;">
  <tr>
    <td colspan="2" style="padding:0 0 14px 0;">
      {{#if signOffImageUrl}}<img src="{{ signOffImageUrl }}" width="150" alt="Kind regards" style="display:block;border:0;">{{/if}}
    </td>
  </tr>
  <tr>
    <td width="86" valign="middle" style="padding:0 16px 0 0;vertical-align:middle;">
      {{#if logoUrl}}<img src="{{ logoUrl }}" width="70" alt="{{ legalEntityName }}" style="display:block;border:0;">{{/if}}
    </td>
    <td valign="top" style="padding:0;">
      <table cellpadding="0" cellspacing="0" border="0" style="width:100%;font-family:Segoe UI,Arial,sans-serif;font-size:12px;color:#333333;">
        <tr>
          <td style="padding:0 0 3px 0;font-size:15px;font-weight:700;color:#29a9e0;">
            {{ fullName }}
          </td>
        </tr>
        <tr>
          <td style="padding:0 0 10px 0;font-size:12px;font-weight:700;color:#333333;">
            {{#if jobTitle}}{{ jobTitle }}{{/if}}{{#if department}}, {{ department }}{{/if}}{{#if designations}}, {{ designations }}{{/if}}
          </td>
        </tr>
        <tr>
          <td style="padding:0 0 10px 0;border-top:1px solid #333333;font-size:0;line-height:0;"></td>
        </tr>
        <tr>
          <td style="padding:0 0 5px 0;">
            {{#if mobile}}<span style="color:#29a9e0;font-weight:700;">M</span> <a href="tel:{{ mobile }}" style="color:#333333;">{{ mobile }}</a>{{/if}}{{#if website}} &nbsp;|&nbsp; <span style="color:#29a9e0;font-weight:700;">W</span> <a href="{{ website }}" style="color:#333333;">{{ website }}</a>{{/if}}
          </td>
        </tr>
        <tr>
          <td style="padding:0 0 5px 0;">
            {{#if directPhone}}<span style="color:#29a9e0;font-weight:700;">D</span> <a href="tel:{{ directPhone }}" style="color:#333333;">{{ directPhone }}</a>{{/if}}
          </td>
        </tr>
        <tr>
          <td style="padding:0 0 5px 0;">
            <span style="color:#29a9e0;font-weight:700;">E</span> <a href="mailto:{{ email }}" style="color:#333333;">{{ email }}</a>
          </td>
        </tr>
        <tr>
          <td style="padding:0 0 12px 0;">
            {{#if addressBlock}}<span style="color:#29a9e0;font-weight:700;">A</span> <span style="color:#333333;text-decoration:underline;">{{ addressBlock }}</span>{{/if}}
          </td>
        </tr>
        <tr>
          <td style="padding:0;">
            {{#if linkedInIcon}}<a href="{{ linkedIn }}" style="text-decoration:none;"><img src="{{ linkedInIcon }}" width="24" height="24" alt="LinkedIn" style="border:0;"></a>&nbsp;{{/if}}{{#if xIcon}}<a href="{{ x }}" style="text-decoration:none;"><img src="{{ xIcon }}" width="24" height="24" alt="X" style="border:0;"></a>&nbsp;{{/if}}{{#if facebookIcon}}<a href="{{ facebook }}" style="text-decoration:none;"><img src="{{ facebookIcon }}" width="24" height="24" alt="Facebook" style="border:0;"></a>&nbsp;{{/if}}{{#if instagramIcon}}<a href="{{ instagram }}" style="text-decoration:none;"><img src="{{ instagramIcon }}" width="24" height="24" alt="Instagram" style="border:0;"></a>&nbsp;{{/if}}{{#if youtubeIcon}}<a href="{{ youtube }}" style="text-decoration:none;"><img src="{{ youtubeIcon }}" width="24" height="24" alt="YouTube" style="border:0;"></a>&nbsp;{{/if}}{{#if whatsappIcon}}<a href="{{ whatsapp }}" style="text-decoration:none;"><img src="{{ whatsappIcon }}" width="24" height="24" alt="WhatsApp" style="border:0;"></a>&nbsp;{{/if}}{{#if telegramIcon}}<a href="{{ telegram }}" style="text-decoration:none;"><img src="{{ telegramIcon }}" width="24" height="24" alt="Telegram" style="border:0;"></a>{{/if}}
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td colspan="2" style="padding:14px 0 0 0;border-top:1px solid #cccccc;font-size:0;line-height:0;"></td>
  </tr>
  <tr>
    <td colspan="2" style="padding:8px 0 0 0;font-size:10px;line-height:14px;color:#999999;">
      {{#if disclaimer}}IMPORTANT: {{ disclaimer }}{{/if}}
    </td>
  </tr>
  <tr>
    <td colspan="2" style="padding:6px 0 0 0;font-size:10px;line-height:14px;color:#999999;">
      {{#if regulatoryLine}}{{ regulatoryLine }}{{/if}}
    </td>
  </tr>
</table>`;

/** The reply variant: name, title, entity, one line. Nobody wants the full block four times. */
const REPLY_BODY = `<table cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;font-family:Segoe UI,Arial,sans-serif;font-size:12px;color:#4a5568;">
  <tr>
    <td style="padding:0;">
      <span style="font-weight:600;color:#0f2b46;">{{ fullName }}</span>{{#if jobTitle}}
      &nbsp;&middot;&nbsp; {{ jobTitle }}{{/if}}
      &nbsp;&middot;&nbsp; <a href="mailto:{{ email }}" style="color:#0f2b46;text-decoration:none;">{{ email }}</a>
    </td>
  </tr>
</table>`;

const PRIMARY_PLAIN = `{{ fullName }}
{{#if jobTitle}}{{ jobTitle }}{{/if}}{{#if department}}, {{ department }}{{/if}}{{#if designations}}, {{ designations }}{{/if}}

{{#if mobile}}M {{ mobile }}{{/if}}{{#if website}} | W {{ website }}{{/if}}
{{#if directPhone}}D {{ directPhone }}{{/if}}
E {{ email }}
{{#if addressBlock}}A {{ addressBlock }}{{/if}}

{{#if disclaimer}}IMPORTANT: {{ disclaimer }}{{/if}}

{{#if regulatoryLine}}{{ regulatoryLine }}{{/if}}`;

const REPLY_PLAIN = `{{ fullName }}{{#if jobTitle}} | {{ jobTitle }}{{/if}} | {{ email }}`;

function template(
  id: string,
  legalEntityId: number,
  legalEntityName: string,
  name: string,
  version: number,
  variant: TemplateVariant,
  isPublished: boolean,
  publishedAtUtc: string | null,
): ISignatureTemplate {
  const isReply = variant === TemplateVariant.Reply;
  return {
    id,
    // Every version of a fixture template shares one family per entity and variant, which is how
    // the seeded data was numbered before templates had families at all.
    familyId: `f-${legalEntityId}-${variant}`,
    legalEntityId,
    legalEntityName,
    name,
    version,
    kind: TemplateKind.Email,
    variant,
    bodyHtml: isReply ? REPLY_BODY : PRIMARY_BODY,
    bodyPlainText: isReply ? REPLY_PLAIN : PRIMARY_PLAIN,
    isPublished,
    publishedAtUtc,
    supersededByTemplateId: null,
    policies: FIXTURE_CATALOGUE.map(p => ({ ...p })),
    requiresApproval: !isReply,
  };
}

/**
 * One published Primary and Reply per entity, plus a BCT draft.
 *
 * The draft matters more than it looks: a published template is immutable, so without one the
 * studio would open read-only every time and the save path would never be reachable.
 */
export function seedTemplates(): ISignatureTemplate[] {
  return [
    template('t-bct-primary', 1, 'BCT', 'BCT — Primary (EN)', 3, TemplateVariant.Primary, true, '2026-07-02T11:20:00Z'),
    template('t-bct-reply', 1, 'BCT', 'BCT — Reply (EN)', 2, TemplateVariant.Reply, true, '2026-07-02T11:20:00Z'),
    template('t-bct-draft', 1, 'BCT', 'BCT — Primary (EN) v4 draft', 4, TemplateVariant.Primary, false, null),
    template('t-adv-primary', 2, 'Adiuvo Trustees', 'Adiuvo — Primary (EN)', 1, TemplateVariant.Primary, true, '2026-05-14T08:05:00Z'),
    template('t-adv-reply', 2, 'Adiuvo Trustees', 'Adiuvo — Reply (EN)', 1, TemplateVariant.Reply, true, '2026-05-14T08:05:00Z'),
    template('t-atc-primary', 3, 'ATCA', 'ATCA — Primary (EL)', 2, TemplateVariant.Primary, true, '2026-06-30T15:40:00Z'),
    template('t-atc-reply', 3, 'ATCA', 'ATCA — Reply (EL)', 2, TemplateVariant.Reply, true, '2026-06-30T15:40:00Z'),
  ];
}

/** How many mailboxes a publish would re-deploy to, per entity. Drives the confirmation dialog. */
export const FIXTURE_PROFILE_COUNTS: Record<number, number> = { 1: 187, 2: 41, 3: 63 };
